import * as React from "react";
import {
    Alert,
    AlertColor,
    Autocomplete,
    Box,
    Stack,
    TextField,
    TextFieldProps,
} from "@mui/material";
import CclDrawer from "../../../../components/common/cclDrawer/cclDrawer";
import CclDrawerActionBar from "../../../../components/common/cclDrawer/cclDrawerActionBar";
import CclLoadingButton from "../../../../components/common/cclButtons/cclLoadingButton";
import CclButton from "../../../../components/common/cclButtons/cclButton";
import { DatePicker } from "@mui/x-date-pickers";
import { SessionDetailType } from "../../../../services/types/sessionDetailTypes";
import {
    useGetAvailableLanguagesQuery,
    useGetSessionSetUpByEventKeyQuery,
} from "../../../../services/cclTokenedSessionApi";
import { useLazyGetCclUserAccountDetailQuery } from "../../../../services/cclTokenedGrandCentralApi";
import { validateEmail } from "../../../../utilities/generalUtilities";
import { useRegisterProductSaleParticipantMutation } from "../../../../services/cclTokenedEnterpriseParticipantApi";
import {
    NewProductSaleRegistration,
    ProductSaleRegistrationRequest,
} from "../../../../services/types/enterpriseParticipantApiTypes";
import { CreateEnterpriseUserRequest } from "../../../../services/types/enterpriseUserApiTypes";
import { useCreateNewEnterpriseUserMutation } from "../../../../services/cclTokenedEnterpriseUserApi";
import useLogAccessEvent from "../../../../hooks/useLogAccessEvent";
import { AccessEventRegistrationDetails } from "../../../../services/types/accessEventTypes";
import { getLocalIsoDateWithNoTime } from "../../../../utilities/dateHelpers";

interface RegisterParticipantDrawerProps {
    open: boolean;
    session: SessionDetailType;
    onClose: () => void;
    showSnackbar: (message: string, severity: AlertColor) => void;
    hasInventory: boolean;
    inventoryIsFetching?: boolean;
}

const searchInputSx = {
    flexGrow: 1,
};

interface LanguageOption {
    label: string;
    id: string;
}

const RegisterParticipantDrawer: React.FC<RegisterParticipantDrawerProps> = (props) => {
    const [languages, setLanguages] = React.useState<LanguageOption[]>([]);
    const { logEvent } = useLogAccessEvent();
    const { data: sessionSetupData, isSuccess } = useGetSessionSetUpByEventKeyQuery(
        props.session.sessionKey.toString(),
        {
            skip: !props.session.sessionKey || props.session.sessionKey <= 0,
        },
    );

    const { data: availableLanguages } = useGetAvailableLanguagesQuery(
        props.session.sessionKey.toString(),
        {
            skip: !props.session.sessionKey || props.session.sessionKey <= 0,
        },
    );

    const [register] = useRegisterProductSaleParticipantMutation();
    const [getUserData] = useLazyGetCclUserAccountDetailQuery();
    const [createUser] = useCreateNewEnterpriseUserMutation();

    const [selectedLanguage, setSelectedLanguage] = React.useState<LanguageOption>({
        label: "Unknown",
        id: "-1",
    });
    const [preworkDate, setPreworkDate] = React.useState<Date | null>(null);
    const [completionDate, setCompletionDate] = React.useState<Date | null>(null);
    const [regInProgress, setRegInProgress] = React.useState<boolean>(false);

    const [email, setEmail] = React.useState<string>("");
    const [firstName, setFirstName] = React.useState<string>("");
    const [lastName, setLastName] = React.useState<string>("");
    const [imKey, setImKey] = React.useState<number | null>(null);
    const [lockNames, setLockNames] = React.useState<boolean>(false);
    const emailDebounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    React.useEffect(() => {
        return () => {
            if (emailDebounceRef.current) clearTimeout(emailDebounceRef.current);
        };
    }, []);

    React.useEffect(() => {
        if (isSuccess && sessionSetupData) {
            setPreworkDate(sessionSetupData.sessionProgramData.preworkInvitationDate);
            setCompletionDate(sessionSetupData.sessionProgramData.surveyCompletionDate);
        }
    }, [isSuccess, sessionSetupData]);

    React.useEffect(() => {
        const newLanguages =
            availableLanguages
                ?.slice() // create a shallow copy to avoid mutating original
                .sort((a: any, b: any) => a.language.localeCompare(b.language))
                .map((lang: any) => ({
                    id: lang.languageKey,
                    label: lang.language,
                })) || [];

        setLanguages(newLanguages);
    }, [availableLanguages]);

    React.useEffect(() => {
        if (languages.length > 0 && selectedLanguage.id === "-1") {
            const englishOption = languages.find((lang) => lang.label === "English");
            if (englishOption) {
                setSelectedLanguage(englishOption);
            }
        }
    }, [languages, selectedLanguage.id]);

    // Helper function to get today at midnight
    const getTodayStart = (): Date => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return today;
    };

    // Validation function that returns error messages
    const validateDateFields = (): string[] => {
        const messages: string[] = [];
        const today = getTodayStart();

        // Safely convert all dates to Date objects and normalize to midnight
        const preworkDateObj = preworkDate ? new Date(preworkDate) : null;
        const normalizedPreworkDate =
            preworkDateObj && !isNaN(preworkDateObj.getTime())
                ? new Date(preworkDateObj.toDateString())
                : null;

        const completionDateObj = completionDate ? new Date(completionDate) : null;
        const normalizedCompletionDate =
            completionDateObj && !isNaN(completionDateObj.getTime())
                ? new Date(completionDateObj.toDateString())
                : null;

        const sessionEndDateObj = sessionSetupData?.sessionProgramData.endDate
            ? new Date(sessionSetupData.sessionProgramData.endDate)
            : null;
        const normalizedSessionEndDate =
            sessionEndDateObj && !isNaN(sessionEndDateObj.getTime())
                ? new Date(sessionEndDateObj.toDateString())
                : null;

        // Invitation (Scheduled): Must be before Assessment Completion and in the future (optional field)
        if (normalizedPreworkDate) {
            if (
                normalizedPreworkDate < today ||
                (normalizedCompletionDate && normalizedPreworkDate >= normalizedCompletionDate)
            ) {
                messages.push(
                    "Invitation (Scheduled) must be before Assessment Completion and in the future",
                );
            }
        }

        // Assessment Completion: Must be before Session End Date and in the future (mandatory field)
        if (normalizedCompletionDate) {
            if (
                normalizedCompletionDate < today ||
                (normalizedSessionEndDate && normalizedCompletionDate >= normalizedSessionEndDate)
            ) {
                messages.push(
                    "Assessment Completion must be before Session End Date and in the future",
                );
            }
        }

        return messages;
    };

    // Compute validation messages synchronously whenever dates change
    const validationMessages = React.useMemo(
        () => validateDateFields(),
        [
            preworkDate,
            completionDate,
            sessionSetupData?.sessionProgramData.endDate,
            validateDateFields,
        ],
    );

    const validateMandatoryFields = (): boolean => {
        // Check all mandatory fields are filled
        if (!email || email.trim() === "") return false;
        if (!firstName || firstName.trim() === "") return false;
        if (!lastName || lastName.trim() === "") return false;
        if (selectedLanguage.id === "-1") return false;
        if (!completionDate) return false;
        return true;
    };

    const clearFields = () => {
        setEmail("");
        setFirstName("");
        setLastName("");
        setImKey(null);
        setLockNames(false);
        // Reset to English language
        const englishOption = languages.find((lang) => lang.label === "English");
        setSelectedLanguage(englishOption || { label: "Unknown", id: "-1" });
        setPreworkDate(sessionSetupData?.sessionProgramData.preworkInvitationDate || null);
        setCompletionDate(sessionSetupData?.sessionProgramData.surveyCompletionDate || null);
    };

    const registerNewParticipant = () => {
        if (completionDate == null) return;
        setRegInProgress(true);
        if (imKey == null || imKey <= 0) {
            let newUserRequest: CreateEnterpriseUserRequest = {
                email: email,
                firstName: firstName,
                lastName: lastName,
            };
            createUser(newUserRequest)
                .unwrap()
                .then((res) => {
                    registerParticipant(res.imKey, completionDate);
                })
                .catch((err) => {
                    props.showSnackbar("Error creating new user", "error");
                    setRegInProgress(false);
                    return;
                });
        } else {
            registerParticipant(imKey, completionDate);
        }
    };

    const registerParticipant = (incomingImKey: number, realCompletionDate: Date) => {
        let newReg: NewProductSaleRegistration = {
            imKey: incomingImKey,
            surveyCompletionDate: getLocalIsoDateWithNoTime(realCompletionDate),
            autoEmailDate: preworkDate ? getLocalIsoDateWithNoTime(preworkDate) : null,
            preferredLanguage: selectedLanguage ? +selectedLanguage.id : -1,
        };

        let request: ProductSaleRegistrationRequest = {
            esKey: props.session.sessionKey,
            newRegistrations: [newReg],
        };

        register(request)
            .unwrap()
            .then((newParticipants) => {
                if (newParticipants.length > 0) {
                    let evtData: AccessEventRegistrationDetails = {
                        esiKey: newParticipants[0].esiKey.toString(),
                        imKey: incomingImKey.toString(),
                        email: newParticipants[0].emailAddress,
                    };
                    logEvent("ParticipantRegistered", evtData);
                    props.showSnackbar("Participant Registered", "success");
                    setRegInProgress(false);
                    clearFields();
                } else {
                    props.showSnackbar("Register Participant Failed", "error");
                    setRegInProgress(false);
                }
            })
            .catch((err) => {
                props.showSnackbar("Register Participant Failed", "error");
                setRegInProgress(false);
            });
    };

    const onEmailChange = (email: string) => {
        setEmail(email);
        if (lockNames) {
            setFirstName("");
            setLastName("");
            setImKey(null);
            setLockNames(false);
        }
        if (emailDebounceRef.current) clearTimeout(emailDebounceRef.current);
        if (!validateEmail(email)) return;
        emailDebounceRef.current = setTimeout(() => {
            getUserData(email)
                .unwrap()
                .then((res) => {
                    if (res.length === 0) return;
                    setFirstName(res[0].firstName);
                    setLastName(res[0].lastName);
                    setImKey(res[0].imKey);
                    setLockNames(true);
                })
                .catch(() => {});
        }, 400);
    };

    return (
        <CclDrawer
            title={"Register Participant"}
            open={props.open}
            onClose={props.onClose}
            width="standard"
        >
            <Stack spacing={3} sx={{ overflow: "hidden", pt: 2 }}>
                <TextField
                    size="small"
                    fullWidth
                    label="Email"
                    value={email}
                    onChange={(e) => onEmailChange(e.target.value)}
                    sx={searchInputSx}
                />

                <TextField
                    size="small"
                    fullWidth
                    label="First Name"
                    value={firstName}
                    disabled={lockNames}
                    onChange={(e) => setFirstName(e.target.value)}
                    sx={searchInputSx}
                />

                <TextField
                    size="small"
                    fullWidth
                    label="Last Name"
                    value={lastName}
                    disabled={lockNames}
                    onChange={(e) => setLastName(e.target.value)}
                    sx={searchInputSx}
                />
                <Autocomplete
                    size="small"
                    options={languages}
                    disableClearable={true}
                    getOptionLabel={(option: any) => option.label}
                    value={selectedLanguage}
                    onChange={(event, newValue) => {
                        if (newValue) {
                            setSelectedLanguage(newValue);
                        } else {
                            setSelectedLanguage({ label: "Unknown", id: "-1" });
                        }
                    }}
                    renderInput={(params) => (
                        <TextField {...params} placeholder="Language" label="Language" />
                    )}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    sx={{ pb: 2 }}
                />

                <DatePicker
                    label={"Invitation (Scheduled)"}
                    value={preworkDate}
                    minDate={getTodayStart()}
                    maxDate={
                        completionDate
                            ? (() => {
                                  const maxDate = new Date(completionDate);
                                  maxDate.setDate(maxDate.getDate() - 1);
                                  return maxDate;
                              })()
                            : undefined
                    }
                    renderInput={(params: JSX.IntrinsicAttributes & TextFieldProps) => {
                        params.fullWidth = true;
                        return (
                            <TextField
                                {...params}
                                size="small"
                                onKeyDown={(e) => {}}
                                sx={{ ...searchInputSx, mb: 2 }}
                            />
                        );
                    }}
                    onChange={(e) => setPreworkDate(e)}
                />

                <DatePicker
                    label={"Assessment Completion"}
                    value={completionDate}
                    minDate={getTodayStart()}
                    maxDate={
                        sessionSetupData?.sessionProgramData.endDate
                            ? (() => {
                                  const maxDate = new Date(
                                      sessionSetupData.sessionProgramData.endDate,
                                  );
                                  maxDate.setDate(maxDate.getDate() - 1);
                                  return maxDate;
                              })()
                            : undefined
                    }
                    renderInput={(params: JSX.IntrinsicAttributes & TextFieldProps) => {
                        params.fullWidth = true;
                        return <TextField {...params} size="small" onKeyDown={(e) => {}} sx={searchInputSx} />;
                    }}
                    onChange={(e) => setCompletionDate(e)}
                />
                {(!props.hasInventory && !regInProgress && !props.inventoryIsFetching) ||
                validationMessages.length > 0 ? (
                    <Box sx={{ mt: 1 }}>
                        {!props.hasInventory && !regInProgress && !props.inventoryIsFetching && (
                            <Alert severity="warning" sx={{ mb: 1 }}>
                                Not enough assessment inventory to register participants.
                            </Alert>
                        )}
                        {validationMessages.map((message, index) => (
                            <Alert key={index} severity="info" sx={{ mb: 1 }}>
                                {message}
                            </Alert>
                        ))}
                    </Box>
                ) : null}
            </Stack>
            <CclDrawerActionBar>
                <CclLoadingButton
                    mode={"primary"}
                    onClick={registerNewParticipant}
                    loading={regInProgress}
                    disabled={
                        !props.hasInventory ||
                        !validateMandatoryFields() ||
                        validationMessages.length > 0
                    }
                >
                    Register
                </CclLoadingButton>
                <CclButton
                    sx={{ float: "right", mr: 4 }}
                    mode={"secondary"}
                    onClick={props.onClose}
                >
                    Cancel
                </CclButton>
            </CclDrawerActionBar>
        </CclDrawer>
    );
};

export default RegisterParticipantDrawer;
